/**
 * HTMLViewer Component
 *
 * Displays HTML content with live preview and source code view.
 * Provides tabs to switch between rendered HTML and source code.
 * Useful for viewing HTML submissions from students.
 */

import React, { useState } from 'react';
import { Box, Typography, Paper, Tabs, Tab, Alert } from '@mui/material';
import { Visibility, Code } from '@mui/icons-material';
import CodeViewer from './CodeViewer';

interface HTMLViewerProps {
  code: string;
  filename?: string;
}

const HTMLViewer: React.FC<HTMLViewerProps> = ({ code, filename }) => {
  const [activeTab, setActiveTab] = useState<'preview' | 'source'>('preview');

  if (!code || code.trim() === '') {
    return (
      <Alert severity="info">
        No HTML content available to display
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header with filename */}
      {filename && (
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {filename}
        </Typography>
      )}

      {/* Tab Navigation */}
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab
            value="preview"
            icon={<Visibility />}
            label="Preview"
            iconPosition="start"
          />
          <Tab
            value="source"
            icon={<Code />}
            label="Source Code"
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Preview Tab */}
      {activeTab === 'preview' && (
        <Box>
          <Typography variant="caption" color="text.secondary" gutterBottom display="block">
            Live HTML Preview (rendered in sandbox)
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              overflow: 'auto',
              minHeight: '400px',
              maxHeight: '600px',
              p: 2,
              bgcolor: 'white'
            }}
          >
            <iframe
              srcDoc={code}
              title="HTML Preview"
              sandbox="allow-scripts allow-same-origin"
              style={{
                width: '100%',
                minHeight: '400px',
                border: 'none',
                backgroundColor: 'white'
              }}
            />
          </Paper>
          <Alert severity="info" sx={{ mt: 2 }}>
            <strong>Security Note:</strong> HTML is rendered in a sandboxed iframe to prevent malicious code execution.
          </Alert>
        </Box>
      )}

      {/* Source Code Tab */}
      {activeTab === 'source' && (
        <Box>
          <CodeViewer
            code={code}
            language="html"
            filename={filename}
            showLineNumbers={true}
          />
        </Box>
      )}
    </Box>
  );
};

export default HTMLViewer;
